import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Copy, Link2, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function StaffPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", name: "", phone: "", email: "" });

  const load = () => api.get("/staff").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ username: "", password: "", name: "", phone: "", email: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ username: s.username, password: "", name: s.name, phone: s.phone || "", email: s.email || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, phone: form.phone, email: form.email };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${editing.id}`, payload);
        toast.success("Personel güncellendi");
      } else {
        await api.post("/staff", form);
        toast.success("Personel eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Personeli silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Personel silindi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const panelUrlFor = (username) => `${window.location.origin}/panel?u=${encodeURIComponent(username)}`;

  const copyUrl = async (username) => {
    const url = panelUrlFor(username);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Giriş URL'si kopyalandı");
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Giriş URL'si kopyalandı"); }
      catch { toast.error("Kopyalanamadı — elle kopyalayın"); }
      finally { ta.remove(); }
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">Personel</h1>
          <p className="text-zinc-400 text-sm mt-1">Panele giriş yapabilecek personelleri buradan yönetin.</p>
        </div>
        <Button onClick={openNew} className="asg-btn-primary rounded-full border-0" data-testid="staff-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Yeni Personel
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.length === 0 && (
          <div className="col-span-full text-center py-16 asg-card rounded-2xl text-zinc-500">
            Henüz personel eklenmedi
          </div>
        )}
        {rows.map((s) => (
          <div key={s.id} className="asg-card rounded-2xl p-6" data-testid={`staff-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-xl">{s.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">@{s.username}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(s)} className="text-zinc-400 hover:text-white" data-testid={`staff-edit-${s.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => del(s.id)} className="text-zinc-400 hover:text-red-400" data-testid={`staff-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 text-sm text-zinc-300 space-y-1">
              {s.phone && <div>📱 {s.phone}</div>}
              {s.email && <div>✉ {s.email}</div>}
            </div>
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                <Link2 className="w-3 h-3" /> Giriş URL'si
              </div>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 min-w-0 text-xs bg-black/30 border border-white/10 rounded-md px-2.5 py-1.5 text-zinc-200 truncate font-mono"
                  data-testid={`staff-url-${s.id}`}
                  title={panelUrlFor(s.username)}
                >
                  {panelUrlFor(s.username)}
                </code>
                <button
                  onClick={() => copyUrl(s.username)}
                  className="shrink-0 h-8 w-8 rounded-md border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white hover:border-white/30 transition-colors"
                  title="Kopyala"
                  data-testid={`staff-copy-url-${s.id}`}
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <a
                  href={panelUrlFor(s.username)}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 h-8 w-8 rounded-md border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white hover:border-white/30 transition-colors"
                  title="Yeni sekmede aç"
                  data-testid={`staff-open-url-${s.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">Bu bağlantıyı personele gönderin — kullanıcı adı otomatik dolu gelir.</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--asg-surface)] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2"><Label>Ad Soyad</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="staff-form-name" />
            </div>
            <div className="grid gap-2"><Label>Kullanıcı Adı</Label>
              <Input value={form.username} onChange={(e) => set("username", e.target.value)} disabled={!!editing} className="bg-[color:var(--asg-bg)] border-white/10 disabled:opacity-60" data-testid="staff-form-username" />
            </div>
            <div className="grid gap-2"><Label>{editing ? "Yeni Şifre (boş bırak = değişmez)" : "Şifre"}</Label>
              <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="staff-form-password" />
            </div>
            <div className="grid gap-2"><Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
            </div>
            <div className="grid gap-2"><Label>E-posta</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-300 hover:text-white">Vazgeç</Button>
            <Button onClick={save} className="asg-btn-primary rounded-full border-0" data-testid="staff-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
